import {
  isRequiredShippingAddressField,
  shippingAddressInputName,
  SHIPPING_ADDRESS_FIELDS,
  type GiftItem,
  type PortalTheme,
  type AddressOnFile,
  type ShippingAddressField,
} from '@/schema/gift-portal.schema'
import type { PortalTranslations } from '../../i18n'
import { portalCssVars, PORTAL_CSS } from '../portal-styles'
import { LangToggle } from '../lang-toggle'

interface SelectPageProps {
  voucherCodeId: string
  items: GiftItem[]
  error?: string
  theme: PortalTheme
  customerName?: string
  preview?: boolean
  translations: PortalTranslations
  lang: string
  allTranslations?: Record<string, PortalTranslations>
  languages?: string[]
  csrfToken?: string
  claimToken?: string
  email?: string
  /** Delivery address on file, for prefill. Null opens the window in edit mode. */
  address?: AddressOnFile | null
  /** Preview only: render the address window in its open state. */
  previewAddressOpen?: boolean
  /** The gift already chosen, when a refused claim comes back to this page. */
  selectedItemId?: string
}

const FORM_ID = 'gift-portal-select-form'
const DIALOG_ID = 'gift-portal-email-dialog'
const ADDRESS_DIALOG_ID = 'gift-portal-address-dialog'
const EMAIL_INPUT_ID = 'gift-portal-email-input'
const SELECTED_ID_INPUT_ID = 'gift-portal-selected-id'

/** i18n key for a field's label — derived, like its input name. */
function addressLabelKey(key: ShippingAddressField): string {
  return `address${key.charAt(0).toUpperCase()}${key.slice(1)}Label`
}

/**
 * Autofill hints, so a browser can fill a saved address in one gesture.
 * The only per-field knowledge here the schema cannot supply.
 *
 * These are HTML spec tokens, not names of our own — there is no
 * `street-name` token and none at all for a house number, and a browser
 * silently ignores anything it does not recognise. `houseNumber` therefore
 * gets no hint: the nearest token, `address-line2`, means a *second address
 * line* (apartment, suite), so a browser honouring it would fill the wrong
 * value in. No hint is better than a wrong one.
 */
const ADDRESS_AUTOCOMPLETE: Partial<Record<ShippingAddressField, string>> = {
  name: 'name',
  postcode: 'postal-code',
  streetName: 'address-line1',
  city: 'address-level2',
  country: 'country-name',
}

export function SelectPage({
  voucherCodeId,
  items,
  error,
  theme,
  customerName,
  preview,
  translations: t,
  lang,
  allTranslations,
  languages,
  csrfToken,
  claimToken,
  email,
  address,
  previewAddressOpen,
  selectedItemId,
}: SelectPageProps) {
  const action = customerName
    ? `/${customerName}/gift-portal/redeem`
    : '/gift-portal/redeem'
  const initialEmail = email || ''
  const hasEmail = initialEmail.length > 0
  const onFile = address || {}
  // Only decides whether the "nothing on file yet" notice replaces the
  // subtitle. The form itself is the same prefilled form either way.
  const hasAddress = Object.values(onFile).some(Boolean)

  return (
    <div className="portal-theme portal-page--wide">
      <style>
        {portalCssVars(theme, { standalone: !preview }) + '\n' + PORTAL_CSS}
      </style>

      {!preview && allTranslations && languages && (
        <LangToggle
          lang={lang}
          allTranslations={allTranslations}
          languages={languages}
        />
      )}

      <form
        id={FORM_ID}
        method="POST"
        action={action}
        className="portal-container"
      >
        <input type="hidden" name="voucherCodeId" value={voucherCodeId} />
        <input type="hidden" name="lang" value={lang} />
        {csrfToken && <input type="hidden" name="_csrf" value={csrfToken} />}
        {claimToken && (
          <input type="hidden" name="claimToken" value={claimToken} />
        )}
        <input
          type="hidden"
          name="email"
          id={EMAIL_INPUT_ID}
          defaultValue={initialEmail}
        />
        <input
          type="hidden"
          name="selectedItemId"
          id={SELECTED_ID_INPUT_ID}
          defaultValue={selectedItemId || ''}
        />
        {SHIPPING_ADDRESS_FIELDS.map((key) => (
          <input
            key={key}
            type="hidden"
            name={shippingAddressInputName(key)}
            data-address-input={key}
          />
        ))}

        {theme.logoUrl && (
          <img src={theme.logoUrl} alt="Logo" className="portal-logo" />
        )}

        <div className="portal-select-header">
          <div className="portal-select-header__intro">
            <h1 className="portal-title" data-i18n="select.title">
              {t.select.title}
            </h1>
            <p className="portal-text" data-i18n="select.subtitle">
              {t.select.subtitle}
            </p>
          </div>

          {/* Header bar: read-only display. "change email" opens the dialog. */}
          <div className="portal-email-bar">
            <div className="portal-email-bar__content">
              <span
                className="portal-email-bar__label"
                data-i18n="select.emailBarLabel"
              >
                {t.select.emailBarLabel}
              </span>
              <span
                className="portal-email-bar__value"
                data-email-value
                data-empty={hasEmail ? '0' : '1'}
              >
                <span
                  className="portal-email-bar__value-actual"
                  data-email-actual
                >
                  {initialEmail}
                </span>
                <span
                  className="portal-email-bar__value-empty"
                  data-i18n="select.emailEmpty"
                >
                  {t.select.emailEmpty}
                </span>
              </span>
            </div>
            <button
              type="button"
              className="portal-email-bar__change"
              data-open-dialog
              data-i18n="select.changeEmail"
            >
              {t.select.changeEmail}
            </button>
          </div>
        </div>

        {error && <div className="portal-alert">{error}</div>}

        {items.length === 0 ? (
          <p className="portal-empty" data-i18n="select.noGifts">
            {t.select.noGifts}
          </p>
        ) : (
          <>
            <div className="portal-grid">
              {items.map((item) => (
                <article
                  key={item.id}
                  className={
                    item.id === selectedItemId
                      ? 'portal-gift portal-gift--selected'
                      : 'portal-gift'
                  }
                  data-gift-id={item.id}
                  data-gift-type={item.type}
                >
                  {item.picture && (
                    <img
                      src={item.picture}
                      alt={item.name}
                      className="portal-gift__img"
                    />
                  )}
                  <div className="portal-gift__body">
                    <h3 className="portal-gift__name">{item.name}</h3>
                    <p className="portal-gift__desc">{item.description}</p>
                  </div>
                  <div className="portal-gift__footer">
                    <button
                      type="button"
                      className="portal-btn portal-btn--full"
                      data-choose-item-id={item.id}
                    >
                      <span
                        className="portal-gift__choose-label"
                        data-i18n="select.chooseButton"
                      >
                        {t.select.chooseButton}
                      </span>
                      <span
                        className="portal-gift__chosen-label"
                        data-i18n="select.chosenButton"
                      >
                        {t.select.chosenButton}
                      </span>
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div className="portal-claim-bar">
              <button
                type="button"
                className="portal-btn portal-claim-bar__btn"
                data-claim-button
                data-i18n="select.selectButton"
                disabled={!selectedItemId}
              >
                {t.select.selectButton}
              </button>
            </div>
          </>
        )}
      </form>

      <dialog id={DIALOG_ID} className="portal-dialog portal-theme">
        <div className="portal-dialog__body">
          <div className="portal-dialog__header">
            <h2
              className="portal-dialog__title"
              data-i18n="select.changeEmailTitle"
            >
              {t.select.changeEmailTitle}
            </h2>
            <p
              className="portal-dialog__subtitle"
              data-i18n="select.changeEmailSubtitle"
            >
              {t.select.changeEmailSubtitle}
            </p>
          </div>

          <label
            className="portal-email-dialog__label"
            data-email-field-wrap
            data-email-invalid="0"
          >
            <span
              className="portal-email-dialog__label-text"
              data-i18n="select.emailLabel"
            >
              {t.select.emailLabel}
            </span>
            <input
              type="email"
              className="portal-email-dialog__input"
              placeholder={t.select.emailPlaceholder}
              data-email-draft-input
              data-i18n-placeholder="select.emailPlaceholder"
            />
            <span
              className="portal-email-dialog__error"
              data-i18n="select.emailInvalid"
            >
              {t.select.emailInvalid}
            </span>
          </label>

          <div className="portal-dialog__footer">
            <button
              type="button"
              className="portal-btn portal-btn--outline"
              data-email-edit-cancel
              data-i18n="select.cancel"
            >
              {t.select.cancel}
            </button>
            <button
              type="button"
              className="portal-btn"
              data-email-edit-save
              data-i18n="select.save"
            >
              {t.select.save}
            </button>
          </div>
        </div>
      </dialog>

      {/* Address window. One state: the delivery address as an editable form,
          prefilled with whatever is on file. Deliberately not a read-only
          "confirm this address" pane with an edit mode behind it — two views
          of one address can disagree, and a read-only pane can offer a
          Confirm button for an address the server will reject (a CSV import
          routinely lands a street and city with no name or postcode). What
          the recipient sees is what gets submitted.
          [data-address-empty] swaps the subtitle for the "nothing on file"
          notice; nothing here is toggled by writing labels. */}
      <dialog
        id={ADDRESS_DIALOG_ID}
        className="portal-dialog portal-theme portal-address-dialog"
        data-address-dialog
        data-address-empty={hasAddress ? '0' : '1'}
        open={preview && previewAddressOpen ? true : undefined}
      >
        <div className="portal-dialog__body">
          <div className="portal-dialog__header">
            <h2
              className="portal-dialog__title"
              data-i18n="select.addressTitle"
            >
              {t.select.addressTitle}
            </h2>
            <p
              className="portal-dialog__subtitle"
              data-i18n="select.addressSubtitle"
            >
              {t.select.addressSubtitle}
            </p>
          </div>

          <p
            className="portal-address-dialog__notice"
            data-i18n="select.addressEmptyNotice"
          >
            {t.select.addressEmptyNotice}
          </p>

          <div className="portal-address-dialog__form">
            {SHIPPING_ADDRESS_FIELDS.map((key) => {
              const required = isRequiredShippingAddressField(key)
              const labelKey = addressLabelKey(key)
              return (
                <label
                  key={key}
                  className="portal-address-dialog__field"
                  data-address-field-wrap={key}
                >
                  <span className="portal-address-dialog__label">
                    {/* The asterisk is a sibling of the translated node, not
                        inside it: LangToggle replaces textContent, which
                        would wipe a marker rendered within. */}
                    <span data-i18n={`select.${labelKey}`}>
                      {t.select[labelKey as keyof typeof t.select]}
                    </span>
                    {required && (
                      <span
                        className="portal-address-dialog__required"
                        aria-hidden="true"
                      >
                        *
                      </span>
                    )}
                  </span>
                  <input
                    type="text"
                    className="portal-address-dialog__input"
                    data-address-draft={key}
                    // reason: the value is repeated as an attribute so
                    // reopening the window can put the field back to what is
                    // on file — Cancel has to discard an edit, and the input's
                    // own `value` is gone the moment it is typed into.
                    data-address-initial={onFile[key] || ''}
                    defaultValue={onFile[key] || ''}
                    required={required}
                    autoComplete={ADDRESS_AUTOCOMPLETE[key]}
                  />
                  {required && (
                    <span
                      className="portal-address-dialog__error"
                      data-i18n="select.addressFieldRequired"
                    >
                      {t.select.addressFieldRequired}
                    </span>
                  )}
                </label>
              )
            })}
          </div>

          <div className="portal-dialog__footer portal-address-dialog__footer">
            <button
              type="button"
              className="portal-btn portal-btn--outline"
              data-address-cancel
              data-i18n="select.cancel"
            >
              {t.select.cancel}
            </button>
            <button
              type="button"
              className="portal-btn"
              data-address-save
              data-i18n="select.addressSave"
            >
              {t.select.addressSave}
            </button>
          </div>
        </div>
      </dialog>

      {!preview && (
        <script
          dangerouslySetInnerHTML={{
            __html: selectPageScript({
              formId: FORM_ID,
              dialogId: DIALOG_ID,
              addressDialogId: ADDRESS_DIALOG_ID,
              emailInputId: EMAIL_INPUT_ID,
              selectedIdInputId: SELECTED_ID_INPUT_ID,
            }),
          }}
        />
      )}
    </div>
  )
}

SelectPage.displayName = 'SelectPage'

/**
 * Inline script for the select page.
 *
 *   - Click a gift's [data-choose-item-id]: mark that card as the current
 *     selection. Stores the id in the hidden <input name="selectedItemId">,
 *     toggles the [.portal-gift--selected] highlight (CSS shows the
 *     "Selected" label and hides "Select" on the matched card), and enables
 *     the Claim button. Sticky: clicking a card never un-selects — only a
 *     different card can replace the selection.
 *   - Click [data-claim-button]: submit the form to /redeem — unless the
 *     email is not valid, in which case the email dialog opens with its error
 *     shown, or the selected card is [data-gift-type="physical"], in which
 *     case the address window opens first and the claim goes through it.
 *   - Click the header's [data-open-dialog] "change email": open the
 *     email-only dialog. Save writes the draft into the hidden
 *     <input name="email"> and updates the header display, but only for a
 *     valid email: an empty or invalid one sets [data-email-invalid] and keeps
 *     the dialog open. Cancel/Escape closes without saving.
 *   - Address window: one prefilled form. Opening it puts every field back to
 *     [data-address-initial] — what is on file — so Cancel discards an edit
 *     rather than leaving it to be submitted by the next open.
 *     [data-address-save] writes the six values into the hidden address
 *     inputs and submits the one form, so gift, email and address arrive in a
 *     single POST. A missing required field is flagged with
 *     [data-address-invalid] on its wrapper, which reveals the translated
 *     message already rendered beside it; the server checks again regardless.
 *
 * Button/placeholder labels are never written from JS — both translations
 * are rendered as sibling spans (each with data-i18n) and CSS picks which
 * one is visible. That way switching languages via LangToggle (which scans
 * data-i18n) re-translates the chosen-state label correctly, and we can't
 * leak a stale snapshot of the labels taken at SSR time.
 *
 * Runs against static SSR HTML — no hydration.
 */
function selectPageScript(opts: {
  formId: string
  dialogId: string
  addressDialogId: string
  emailInputId: string
  selectedIdInputId: string
}): string {
  return `
(function() {
  var form = document.getElementById(${JSON.stringify(opts.formId)});
  var dialog = document.getElementById(${JSON.stringify(opts.dialogId)});
  var emailInput = document.getElementById(${JSON.stringify(
    opts.emailInputId,
  )});
  var selectedInput = document.getElementById(${JSON.stringify(
    opts.selectedIdInputId,
  )});
  var addressDialog = document.getElementById(${JSON.stringify(
    opts.addressDialogId,
  )});
  if (!form || !dialog || !emailInput || !selectedInput) return;
  var claimButton = form.querySelector('[data-claim-button]');
  var submitting = false;
  var emailWrap = dialog.querySelector('[data-email-field-wrap]');
  // The browser's own email check; the server's Zod check is the one that holds.
  var emailProbe = document.createElement('input');
  emailProbe.type = 'email';
  emailProbe.required = true;

  function isValidEmail(value) {
    emailProbe.value = value;
    return emailProbe.checkValidity();
  }

  function markEmailField(invalid) {
    if (emailWrap) emailWrap.setAttribute('data-email-invalid', invalid ? '1' : '0');
  }

  function updateEmailEverywhere(value) {
    emailInput.value = value;
    var hasEmail = value.length > 0;
    document.querySelectorAll('[data-email-value]').forEach(function(el) {
      el.setAttribute('data-empty', hasEmail ? '0' : '1');
      var actual = el.querySelector('[data-email-actual]');
      if (actual) actual.textContent = value;
    });
  }

  function selectGift(itemId) {
    if (!itemId) return;
    if (selectedInput.value === itemId) return;
    selectedInput.value = itemId;
    document.querySelectorAll('[data-gift-id]').forEach(function(card) {
      var match = card.getAttribute('data-gift-id') === itemId;
      card.classList.toggle('portal-gift--selected', match);
    });
    if (claimButton) claimButton.disabled = false;
  }

  function openDialog() {
    var draft = dialog.querySelector('[data-email-draft-input]');
    if (draft) draft.value = emailInput.value;
    markEmailField(false);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    if (draft) draft.focus();
  }

  function closeDialog() {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function saveDialog() {
    var draft = dialog.querySelector('[data-email-draft-input]');
    if (!draft) return;
    var value = (draft.value || '').trim();
    if (!isValidEmail(value)) {
      markEmailField(true);
      draft.focus();
      return;
    }
    updateEmailEverywhere(value);
    closeDialog();
  }

  function selectedGiftType() {
    var card = form.querySelector(
      '[data-gift-id="' + selectedInput.value + '"]'
    );
    return card ? card.getAttribute('data-gift-type') : null;
  }

  function submitClaim() {
    if (submitting) return;
    submitting = true;
    document.querySelectorAll('[data-choose-item-id]').forEach(function(el) {
      el.disabled = true;
    });
    if (claimButton) claimButton.disabled = true;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  /** Back to what is on file, and clear any leftover error marks. */
  function resetAddressDrafts() {
    addressDialog.querySelectorAll('[data-address-draft]').forEach(
      function(draft) {
        draft.value = draft.getAttribute('data-address-initial') || '';
        markAddressField(draft, false);
      }
    );
  }

  function openAddressDialog() {
    if (!addressDialog) return;
    resetAddressDrafts();
    if (typeof addressDialog.showModal === 'function')
      addressDialog.showModal();
    else addressDialog.setAttribute('open', '');
    focusFirstAddressField();
  }

  function closeAddressDialog() {
    if (!addressDialog) return;
    if (typeof addressDialog.close === 'function') addressDialog.close();
    else addressDialog.removeAttribute('open');
  }

  function focusFirstAddressField() {
    var first = addressDialog.querySelector('[data-address-draft]');
    if (first) first.focus();
  }

  /** Copy the six drafts into the hidden inputs the single POST carries. */
  function writeAddressToForm() {
    addressDialog.querySelectorAll('[data-address-draft]').forEach(
      function(draft) {
        var key = draft.getAttribute('data-address-draft');
        var target = form.querySelector('[data-address-input="' + key + '"]');
        if (target) target.value = (draft.value || '').trim();
      }
    );
  }

  function markAddressField(draft, invalid) {
    var wrap = draft.closest('[data-address-field-wrap]');
    if (wrap) wrap.setAttribute('data-address-invalid', invalid ? '1' : '0');
  }

  /**
   * Claim with what the recipient typed.
   *
   * A missing required field is reported by the message already rendered
   * beside it, which CSS reveals — not by the browser's native bubble, whose
   * text comes from the browser's language rather than the portal's. Still
   * no label is written from JS.
   */
  function saveAddressAndClaim() {
    if (!addressDialog) return;
    var drafts = addressDialog.querySelectorAll('[data-address-draft]');
    var firstEmpty = null;
    for (var i = 0; i < drafts.length; i++) {
      if (!drafts[i].required) continue;
      var empty = !(drafts[i].value || '').trim();
      markAddressField(drafts[i], empty);
      if (empty && !firstEmpty) firstEmpty = drafts[i];
    }
    if (firstEmpty) {
      firstEmpty.value = '';
      firstEmpty.focus();
      return;
    }
    writeAddressToForm();
    closeAddressDialog();
    submitClaim();
  }

  document.addEventListener('click', function(e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    var chooseTrigger = target.closest('[data-choose-item-id]');
    if (chooseTrigger) {
      if (submitting) return;
      selectGift(chooseTrigger.getAttribute('data-choose-item-id') || '');
      return;
    }

    if (target.closest('[data-claim-button]')) {
      if (submitting) return;
      if (!selectedInput.value) return;
      if (!isValidEmail(emailInput.value)) {
        openDialog();
        markEmailField(true);
        return;
      }
      if (addressDialog && selectedGiftType() === 'physical') {
        openAddressDialog();
        return;
      }
      submitClaim();
      return;
    }

    if (target.closest('[data-open-dialog]')) {
      openDialog();
      return;
    }

    if (target.closest('[data-email-edit-cancel]')) {
      closeDialog();
      return;
    }

    if (target.closest('[data-email-edit-save]')) {
      saveDialog();
      return;
    }

    if (target.closest('[data-address-cancel]')) {
      closeAddressDialog();
      return;
    }

    if (target.closest('[data-address-save]')) {
      saveAddressAndClaim();
      return;
    }
  });

  document.addEventListener('input', function(e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-email-draft-input]')) {
      if (isValidEmail((target.value || '').trim())) markEmailField(false);
      return;
    }
    if (!target.matches('[data-address-draft]')) return;
    if ((target.value || '').trim()) markAddressField(target, false);
  });

  document.addEventListener('keydown', function(e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-email-draft-input]')) {
      if (e.key === 'Enter') { e.preventDefault(); saveDialog(); }
      if (e.key === 'Escape') { e.preventDefault(); closeDialog(); }
      return;
    }
    if (target.matches('[data-address-draft]')) {
      if (e.key === 'Enter') { e.preventDefault(); saveAddressAndClaim(); }
      if (e.key === 'Escape') { e.preventDefault(); closeAddressDialog(); }
    }
  });

  // Trimmed the way the server trims, so a stored " jan@x.nl " still claims.
  updateEmailEverywhere((emailInput.value || '').trim());
})();
`
}
